import React, { useState, useEffect, useMemo } from 'react';
import {
    Paper,
    Title,
    Select,
    Box,
    Alert,
    LoadingOverlay,
    Group,
    Text,
    Card,
    ActionIcon,
    Tooltip,

} from '@mantine/core';
import { TransferList } from '../common/TransferList';
import { IconFlag } from '@tabler/icons-react';
import { IconUsers, IconRefresh } from '@tabler/icons-react';
import useAuthenticatedData from '../../hooks/useAuthenticatedData';
import useAuthenticatedPost from '../../hooks/useAuthenticatedPost';
import useAuthenticatedDelete from '../../hooks/useAuthenticatedDelete';
import config from '../../config';



export default function GroupUserManagement() {
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [transferData, setTransferData] = useState([[], []]);
    const [refreshKey, setRefreshKey] = useState(0);

    const { data: groups, isLoading: groupsLoading, error: groupsError } =
        useAuthenticatedData(`${config.apiBaseUrl}/Admin/groups/`);

    const { data: availableUsers, isLoading: availableLoading, error: availableError } =
        useAuthenticatedData(
            selectedGroupId
                ? `${config.apiBaseUrl}/Admin/groups/${selectedGroupId}/available-users`
                : null,
            { refreshKey }
        );

    const { data: groupUsers, isLoading: usersLoading, error: usersError } =
        useAuthenticatedData(
            selectedGroupId
                ? `${config.apiBaseUrl}/Admin/groups/${selectedGroupId}/users`
                : null,
            { refreshKey }
        );

    const { post: addUserToGroup, isLoading: addLoading, error: addError } = useAuthenticatedPost();
    const { delete: removeUserFromGroup, isLoading: removeLoading, error: removeError } = useAuthenticatedDelete();

    const isLoading = groupsLoading || availableLoading || usersLoading || addLoading || removeLoading;
    const mutationError = addError || removeError;

    const transform = (users) => {
        if (!Array.isArray(users)) return [];
        return users
            .filter(u => u && u.pentagonCCode != null)
            .map(u => ({
                value: u.pentagonCCode,
                label: u.fullName || 'Unknown User',
                description: u.email || '',
            }));
    };

    useEffect(() => {
        if (selectedGroupId && availableUsers && groupUsers) {
            setTransferData([transform(availableUsers), transform(groupUsers)]);
        } else {
            setTransferData([[], []]);
        }
    }, [selectedGroupId, availableUsers, groupUsers]);

    const handleGroupChange = (id) => {
        setSelectedGroupId(id);
        setTransferData([[], []]);
    };

    const handleTransfer = async ([newAvailable, newAssigned]) => {
        if (!selectedGroupId) return;
        const [oldAvailable, oldAssigned] = transferData;
        const added = newAssigned.filter(u => !oldAssigned.some(o => o.value === u.value));
        const removed = newAvailable.filter(u => !oldAvailable.some(o => o.value === u.value));

        try {
            for (let u of added) {
                await addUserToGroup(`${config.apiBaseUrl}/Admin/groups/${selectedGroupId}/users/${u.value}`, {});
            }
            for (let u of removed) {
                await removeUserFromGroup(`${config.apiBaseUrl}/Admin/groups/${selectedGroupId}/users/${u.value}`);
            }
            setTransferData([newAvailable, newAssigned]);
            setRefreshKey(k => k + 1);
        } catch {
            setRefreshKey(k => k + 1);
        }
    };

    const groupOptions = useMemo(
        () => groups?.map(g => ({ value: `${g.id}`, label: g.name })) || [],
        [groups]
    );

    return (
        <Paper shadow="md" p="xl" style={{ maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
            <Group justify="space-between" mb="lg">
                <Group>
                    <IconUsers size={32} />
                    <Title order={1}>Group User Management</Title>
                </Group>
                <Tooltip label="Refresh data">
                    <ActionIcon onClick={() => setRefreshKey(k => k + 1)} disabled={isLoading}>
                        <IconRefresh />
                    </ActionIcon>
                </Tooltip>
            </Group>

            <Card withBorder mb="lg" p="md">
                <Title order={3} mb="md">Select Group</Title>
                <Select
                    placeholder="Choose a group to manage"
                    data={groupOptions}
                    value={selectedGroupId}
                    onChange={handleGroupChange}
                    searchable
                    clearable
                    disabled={groupsLoading}
                    styles={{
                        option: { color: '#000' },
                        dropdown: { backgroundColor: '#fff' }
                    }}
                    maxDropdownHeight={300}
                    withinPortal
                    dropdownPosition="bottom"
                />
                {groupsError && <Alert color="red">Error loading groups: {groupsError.message}</Alert>}
            </Card>

            {selectedGroupId ? (
                <Card withBorder p="md" style={{ position: 'relative' }}>
                    <LoadingOverlay visible={isLoading} />
                    <Title order={3} mb="md"  >Manage Group Members</Title>

                    {(availableError || usersError || mutationError) && (
                        <Alert color="red" mb="md">
                            Error: {availableError?.message || usersError?.message || mutationError?.message}
                        </Alert>
                    )}

                    <TransferList
                        value={transferData}
                        onChange={handleTransfer}
                        titles={['Available Users', 'Group Members']}
                        listHeight={400}
                        filter={(q, item) =>
                            item.label.toLowerCase().includes(q.toLowerCase()) ||
                            item.description.toLowerCase().includes(q.toLowerCase())
                        }
                        disabled={isLoading}
                        searchPlaceholder="Search users..."

                    />
                </Card>
            ) : (
                <Card withBorder p="md">
                    <Group>
                        <IconUsers size={48} style={{ opacity: 0.5 }} />
                        <Box>
                            <Title order={4}>Get Started</Title>
                            <Text  >
                                Select a group from the dropdown above to start managing its members.
                            </Text>
                        </Box>
                    </Group>
                </Card>
            )}
        </Paper>
    );
}
